import React, { useState } from 'react';
import { IonIcon, IonInput } from '@ionic/react';
import { checkmarkOutline, closeOutline } from 'ionicons/icons';
import {
  OTHER_OPTION,
  TemplateLogic,
  type ContextVars,
  type TemplateField,
} from '@/modules/template';
import { pxToRem } from '@/modules/config/constants';

// Extracted verbatim from ComposeWithTemplate.tsx (#447) so news steps can
// reuse the field/option editor with its full "Other..." lifecycle.

// Fallback prompt for a free-text field input when the template supplies no help
// text of its own — so the input never renders as a blank, invisible box.
export const CUSTOM_TEXT_PLACEHOLDER = 'Enter your custom text here';

export interface FieldEditorProps {
  field: TemplateField;
  value: string;
  context: ContextVars;
  singletonMode: boolean;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onValueCommit?: (value: string) => void;
  onOtherInputSubmit?: (value: string) => void;
}

export const FieldEditor: React.FC<FieldEditorProps> = ({
  field,
  value,
  context,
  singletonMode,
  disabled,
  onValueChange,
  onValueCommit,
  onOtherInputSubmit,
}) => {
  const editorOptions = TemplateLogic.editorOptions(field, singletonMode);

  if (editorOptions.length === 0) {
    return (
      <div>
        <FieldLabel field={field} context={context} />
        <IonInput
          value={value}
          onIonInput={e => onValueChange(e.detail.value ?? '')}
          placeholder={field.help || CUSTOM_TEXT_PLACEHOLDER}
          maxlength={field.maxLength}
          disabled={disabled}
          data-testid={`share-user-input-${field.id}`}
        />
      </div>
    );
  }

  return (
    <UserPickField
      field={{ ...field, options: editorOptions }}
      value={value}
      context={context}
      disabled={disabled}
      onValueChange={onValueChange}
      onValueCommit={onValueCommit}
      onOtherInputSubmit={onOtherInputSubmit}
    />
  );
};

export interface UserPickFieldProps {
  field: TemplateField;
  value: string;
  context: ContextVars;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  onValueCommit?: (value: string) => void;
  onOtherInputSubmit?: (value: string) => void;
}

export const UserPickField: React.FC<UserPickFieldProps> = ({
  field,
  value,
  context,
  disabled,
  onValueChange,
  onValueCommit,
  onOtherInputSubmit,
}) => {
  const options = field.options ?? [];
  const allowsOther = TemplateLogic.fieldAllowsOther(field);
  const literalOptions = options.filter(o => o !== OTHER_OPTION);

  const matchesValue = (opt: string) =>
    opt === value ||
    (value.length > 0 &&
      TemplateLogic.resolveOptionValue(opt, context) === value);

  const baseOptionSelected = literalOptions.find(matchesValue) ?? null;
  // Gate the "Other" free-text against values that are a declared option in a
  // mode that isn't currently offered — e.g. a singleton greeting token like
  // "Hey {_recipientInformalName}" viewed in group mode. Without this, the raw
  // token leaks into the free-text input as its default text. allOptions()
  // spans both modes and is a superset of literalOptions, so this single search
  // also covers everything baseOptionSelected would match.
  const nonOtherOptionSelected =
    TemplateLogic.allOptions(field)
      .filter(o => o !== OTHER_OPTION)
      .find(matchesValue) ?? null;
  const initialOtherActive =
    allowsOther && !nonOtherOptionSelected && value.length > 0;
  const [otherActive, setOtherActive] = useState(initialOtherActive);

  // The literal option that was selected before the user switched to free-text
  // entry. Cancelling the "Other..." input restores it (or the first option
  // when nothing was selected) so the field is never left blank.
  const [lastKnownOption, setLastKnownOption] = useState<string | null>(
    baseOptionSelected
  );

  const showOtherInput =
    allowsOther &&
    (otherActive || (!nonOtherOptionSelected && value.length > 0));

  const submitOtherInput = (submittedValue: string) => {
    onValueChange(submittedValue);
    onOtherInputSubmit?.(submittedValue);
  };

  // Cancelling falls back to the last selected option, then the first option.
  const cancelOtherInput = () => {
    setOtherActive(false);
    const fallbackOption = lastKnownOption ?? literalOptions[0] ?? null;
    if (fallbackOption) {
      onValueChange(fallbackOption);
      onValueCommit?.(fallbackOption);
    } else {
      onValueChange('');
    }
  };

  const handleOtherInputKeyDown = (
    e: React.KeyboardEvent<HTMLIonInputElement>
  ) => {
    if (e.nativeEvent.key !== 'Enter') return;

    e.preventDefault();
    e.nativeEvent.preventDefault();
    const submittedValue = String(e.currentTarget.value ?? value);
    const inputElement = e.currentTarget.getInputElement();
    submitOtherInput(submittedValue);
    void inputElement.then(input => input.blur());
  };

  return (
    <div>
      <FieldLabel field={field} context={context} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {literalOptions.map(opt => {
          const active = opt === baseOptionSelected && !otherActive;
          return (
            <Chip
              key={opt}
              active={active}
              activeTone="preset"
              disabled={disabled}
              label={TemplateLogic.resolveOptionValue(opt, context)}
              testId={`share-user-option-${field.id}-${slug(opt)}`}
              onClick={() => {
                setOtherActive(false);
                const nextValue = active ? '' : opt;
                onValueChange(nextValue);
                if (nextValue) {
                  onValueCommit?.(nextValue);
                }
              }}
            />
          );
        })}
        {allowsOther && (
          <Chip
            active={showOtherInput}
            activeTone="user"
            disabled={disabled}
            label={field.otherLabel ?? 'Other...'}
            testId={`share-user-option-${field.id}-other`}
            onClick={() => {
              // Capture the current selection so the input's x can restore it.
              if (!showOtherInput) {
                setLastKnownOption(baseOptionSelected);
              }
              setOtherActive(!showOtherInput);
              onValueChange('');
            }}
          />
        )}
      </div>
      {showOtherInput && (
        <div
          style={{
            marginTop: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <IonInput
            style={{ flex: 1 }}
            value={nonOtherOptionSelected ? '' : value}
            onIonInput={e => onValueChange(e.detail.value ?? '')}
            onKeyDown={handleOtherInputKeyDown}
            placeholder={field.help || CUSTOM_TEXT_PLACEHOLDER}
            maxlength={field.maxLength}
            disabled={disabled}
            data-testid={`share-user-input-${field.id}`}
          />
          <OtherInputAction
            icon={closeOutline}
            tone="cancel"
            ariaLabel="Cancel custom text"
            disabled={disabled}
            testId={`share-user-input-${field.id}-cancel`}
            onClick={cancelOtherInput}
          />
          <OtherInputAction
            icon={checkmarkOutline}
            tone="confirm"
            ariaLabel="Confirm custom text"
            disabled={disabled}
            testId={`share-user-input-${field.id}-confirm`}
            onClick={() => submitOtherInput(value)}
          />
        </div>
      )}
    </div>
  );
};

export const Label: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      color: '#999',
      fontSize: pxToRem(11),
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      marginBottom: '4px',
    }}
  >
    {text}
  </div>
);

export const FieldLabel: React.FC<{
  field: TemplateField;
  context: ContextVars;
}> = ({ field, context }) => {
  const help = field.help?.trim();

  return (
    <div style={{ marginBottom: '4px' }}>
      <Label text={TemplateLogic.fieldLabel(field, context)} />
      {help && (
        <div
          style={{
            color: '#777',
            fontSize: pxToRem(14),
            lineHeight: 1.25,
            marginTop: '-2px',
          }}
        >
          {help}
        </div>
      )}
    </div>
  );
};

interface ChipProps {
  active: boolean;
  activeTone?: 'preset' | 'user';
  disabled?: boolean;
  label: string;
  testId: string;
  onClick: () => void;
}

export const Chip: React.FC<ChipProps> = ({
  active,
  activeTone = 'preset',
  disabled,
  label,
  testId,
  onClick,
}) => {
  const activeColor =
    activeTone === 'user'
      ? {
          border: 'var(--ion-color-secondary, #007bff)',
          background: 'rgba(0, 123, 255, 0.1)',
          text: 'var(--ion-color-secondary, #007bff)',
        }
      : {
          border: '#D85A30',
          background: '#FAECE7',
          text: '#993C1D',
        };

  return (
    <button
      type="button"
      data-testid={testId}
      data-selected={active ? 'true' : 'false'}
      disabled={disabled}
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '5px 10px',
        borderRadius: '16px',
        border: active
          ? `1.5px solid ${activeColor.border}`
          : '0.5px solid #ccc',
        background: active ? activeColor.background : 'transparent',
        fontSize: pxToRem(15),
        color: active ? activeColor.text : '#444',
      }}
    >
      {label}
    </button>
  );
};

interface OtherInputActionProps {
  icon: string;
  tone: 'cancel' | 'confirm';
  ariaLabel: string;
  disabled?: boolean;
  testId: string;
  onClick: () => void;
}

// The small round x / check buttons that sit beside the "Other..." free-text
// input so it's clear how to discard or commit the custom value.
export const OtherInputAction: React.FC<OtherInputActionProps> = ({
  icon,
  tone,
  ariaLabel,
  disabled,
  testId,
  onClick,
}) => {
  // Map to Ionic's semantic color roles (shared across the ios/md modes) rather
  // than the brand accent: green "success" reads as confirm, neutral "medium"
  // as a non-destructive cancel.
  const color =
    tone === 'confirm'
      ? {
          border: 'var(--ion-color-success, #2dd36f)',
          text: 'var(--ion-color-success-shade, #28ba62)',
        }
      : {
          border: 'var(--ion-color-medium, #92949c)',
          text: 'var(--ion-color-medium-shade, #808289)',
        };

  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      style={{
        all: 'unset',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: pxToRem(32),
        height: pxToRem(32),
        borderRadius: '50%',
        border: `1px solid ${color.border}`,
        color: color.text,
        fontSize: pxToRem(18),
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <IonIcon icon={icon} aria-hidden="true" />
    </button>
  );
};

export function slug(s: string): string {
  return s
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase()
    .replace(/^-|-$/g, '');
}
